'use client';

import { addUserPres, fetchCustomerPrescription, updateUserPres } from '@/api/account';
import {
  EDIT_PRESCRIPTION_COMPONENT,
  ZL_PRESCRIPTION_DATA,
} from '@/app/_lens-components/constants';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { CustomerPrescription, ILoadPR } from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  preIsSame,
  removePrescriptionPlus,
} from '@/app/_lens-components/utils';
import LearnPrescriptionDialog, {
  PrescriptionParamType,
} from '@/app/account/my-prescriptions/add-prescription/components/learn-prescription-dialog';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Checkbox from '@/components/Form/Checkbox';
import Input from '@/components/Form/FormInput';
import Select from '@/components/Form/Select';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import PopupResponsive from '@/components/PopupResponsive';
import PrescriptionForm from '@/components/PrescriptionFormV2/PrescriptionFormExtra';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import UnionDialog from '@/components/PrescriptionFormV2/UnionDialog';
import {
  checkPrescription,
  getEmptyPrescription,
  validateUnion,
} from '@/components/PrescriptionFormV2/utils';
import PrescriptionTable from '@/components/PrescriptionTable';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { hasTwoPd } from '@/utils/prescription';
import { setSession } from '@/utils/tools';
import { SelectChangeEvent, useMediaQuery } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { CheckOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import LeaveMsg from './LeaveMsg';
import PrescriptionRecognition, { AbTestingData } from './PrescriptionRecognition';
import { PRESCRIPTION_NAME_FORMAT_INPUT, PRESCRIPTION_NAME_PATTERN } from '@/utils/constants';

import { font } from '@/theme/zeelool.theme';

interface EditPrescriptionProps {
  prescriptionType: EPrescriptionType;
  onSubmitPrescription: (data: any) => void;
  nowComponent: string;
  abTestingData?: AbTestingData;
}

// 定义组件ref类型
export interface EditPrescriptionRef {
  confirmPrescription: () => Promise<void>;
}

const EditPrescription = forwardRef<EditPrescriptionRef, EditPrescriptionProps>(
  ({ prescriptionType, onSubmitPrescription, nowComponent, abTestingData }, ref) => {
    const t = useTranslations('lens-process');
    const { deviceType } = useContext(ResponsiveContext);
    const isWap = useMediaQuery('(max-width:1280px)');
    const currency = useStore((state) => state.currency);
    // ===== 全局状态 =====
    const token = useStore((state) => state.token);
    const userInfo = useStore((state) => state.userInfo);
    const publicResourceData = useStore((state) => state.publicResourceData);
    const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
    const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
    const { trackEvent } = useReporting();
    const {
      setPrescriptionType,
      setPrescription,
      setPrescriptionsImage,
      presTypeOptionName,
      lensGoodsItem,
      lensGoodsSensorsData,
      setScanInfo,
      scanInfo,
      goodsFramePriceInfo,
    } = useLensProcessStore();

    // 处方识别功能开关状态
    const [enablePrescriptionRecognition, setEnablePrescriptionRecognition] = useState(false);
    // 引导说明开关
    const [enablePrescriptionRecognitionNoviceGuide, setEnablePrescriptionRecognitionNoviceGuide] =
      useState(false);

    // 加载动画时长
    const [prLoadAnimationTime, setPrLoadAnimationTime] = useState(15);
    useEffect(() => {
      const {
        jsonvalues: {
          prescription_recognition_open: {
            prescription_recognition_open_wap,
            prescription_recognition_open_pc,
            prescription_recognition_novice_guide_open_wap,
            prescription_recognition_novice_guide_open_pc,
            load_animation_time,
          },
        },
      } = publicResourceData;
      const enableRecognition =
        (deviceType === EPlatform.WEB
          ? prescription_recognition_open_pc
          : prescription_recognition_open_wap) === 1;
      setEnablePrescriptionRecognition(enableRecognition);
      setEnablePrescriptionRecognitionNoviceGuide(
        (deviceType === EPlatform.WEB
          ? prescription_recognition_novice_guide_open_pc
          : prescription_recognition_novice_guide_open_wap) === 1,
      );
      setPrLoadAnimationTime(load_animation_time);

      SD_PrescriptionEnterPage_View(enableRecognition);
    }, [publicResourceData]);

    // ===== 处方相关状态 =====
    const [prescriptionOptions, setPrescriptionOptions] = useState<IPrescriptionOptions>(
      () => getEmptyPrescription() as IPrescriptionOptions,
    );
    const [saveMyPres, setSaveMyPres] = useState(false); // 是否保存处方

    // ===== 用户处方列表相关状态 =====
    const [myPresList, setMyPresList] = useState<CustomerPrescription[]>([]);
    const [selectedPres, setSelectedPres] = useState<CustomerPrescription>(null);
    const [isLoading, setIsLoading] = useState(false);

    // ===== 弹窗相关状态 =====
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isSelectPresDialogOpen, setIsSelectPresDialogOpen] = useState(false);
    const [selectedPresInPopup, setSelectedPresInPopup] = useState<CustomerPrescription>(null);
    const [learnPrescriptionDialogOpen, setLearnPrescriptionDialogOpen] = useState(false);
    const [learnPrescriptionTab, setLearnPrescriptionTab] = useState<PrescriptionParamType>('sph');
    const [leaveMsgDialogOpen, setLeaveMsgDialogOpen] = useState(false);
    const [unionDialogOpen, setUnionDialogOpen] = useState(false);

    // ===== 表单控制 =====
    const { control, setValue, trigger, getValues, setError } = useForm({
      defaultValues: {
        prescription: JSON.stringify(getEmptyPrescription()),
        prescriptionName: '',
      },
    });

    // ===== 登录相关函数 =====
    const handleOpenLogin = (eventType: string, situation?: string) => {
      setShowLoginRegisterDialog({
        show: true,
        eventType,
        ...(situation && { situation }),
      });
    };

    // ===== 处方数据处理函数 =====
    // 重置处方数据
    const resetData = () => {
      const emptyPrescription = getEmptyPrescription() as IPrescriptionOptions;
      setPrescriptionOptions(emptyPrescription);
      setValue('prescription', JSON.stringify(emptyPrescription));
    };
    logger.log('prescriptionOptions', resetData);

    const scrollToError = () => {
      const errorElement = document.querySelector('.form-error-text');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // 处方表单选项打开回调
    const handlePrescriptionSelectionOpen = (key: string) => {
      SDPrescriptionEnterPageParamValueClick(key);
    };

    // 处理保存处方选项变更
    const handleSaveMyPresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSaveMyPres(checked);

      if (checked && !token) {
        handleOpenLogin('prescription', '保存我的处方');
      }
    };

    // 用户在选择处方后是否有修改
    const isChangedViaCompareUserPres = () => {
      if (!selectedPres || !selectedPres.id) return false;
      const selectedPrescription = selectedPres.prescription || {};
      const selectedPrescriptionKeys = Object.keys(selectedPrescription);
      // 检查每个键的值是否相同
      for (const key of selectedPrescriptionKeys) {
        if (prescriptionOptions[key] !== selectedPrescription[key]) {
          return true;
        }
      }
      return getValues('prescriptionName') !== selectedPres.name;
    };

    // 保存处方到用户账户
    const savePrescriptionToAccount = async (prescriptionData: IPrescriptionOptions) => {
      if (!saveMyPres) return;

      try {
        setIsGlobalLoading(true);
        const submitData = {
          ...removePrescriptionPlus({ ...prescriptionData }),
          prescription_type: getApiPrescriptionType(prescriptionType),
          name: getValues('prescriptionName'),
          prescriptions_image: scanInfo?.imageUrl || '',
        };
        // 若没有扫描处方+选择了用户处方，并且用户没有修改处方，则使用用户处方的图片
        if (!submitData.prescriptions_image && selectedPres?.id && !isChangedViaCompareUserPres()) {
          // @ts-ignore
          submitData.prescriptions_image = selectedPres?.prescriptions_image || '';
        }

        if (selectedPres?.id) {
          await updateUserPres(selectedPres.id, submitData);
        } else {
          const addData = { ...submitData, pre_scan_id: undefined };
          if (
            preIsSame(prescriptionData, {
              ...getEmptyPrescription(),
              ...selectedPres?.prescription,
            }) &&
            scanInfo?.recordId
          ) {
            addData.pre_scan_id = scanInfo.recordId;
          }
          const { code, msg } = await addUserPres(addData);
          if (code !== 200) {
            const nameError = msg || t('failedToSavePrescription');
            toast.error(nameError);
            setError('prescriptionName', {
              message: nameError,
            });
            return false;
          }
        }
        return true;
      } catch (error) {
        logger.error('Failed to save prescription', error);
        toast.error(error.msg || t('failedToSavePrescription'));
        return false;
      } finally {
        setIsGlobalLoading(false);
      }
    };

    // ===== 用户处方列表相关函数 =====
    // 获取用户处方列表
    const getUserPresList = async () => {
      try {
        setIsLoading(true);
        const { data } = await fetchCustomerPrescription({
          page: 1,
          limit: 100,
          prescription_type: getApiPrescriptionType(prescriptionType),
        });
        if (data?.list?.data?.length) {
          setMyPresList(data.list.data);
          setSelectedPresInPopup(data.list.data[0]);
        }
      } catch (error) {
        logger.error('获取用户处方列表失败', error);
      } finally {
        setIsLoading(false);
      }
    };

    // 选择处方
    const chooseMyPrescription = () => {
      if (isLoading) {
        return;
      }

      if (!token) {
        handleOpenLogin('account');
        return;
      }

      if (myPresList.length > 0) {
        isWap && setIsSelectPresDialogOpen(true);
      } else if (myPresList.length === 0) {
        // 提示用户没有保存的处方
        toast.warning(t('noPrescriptionTip'));
      }
    };

    // 清除已选处方
    const clearSelectPres = () => {
      setSelectedPres(null);
      // 重置处方数据
      setPrescriptionOptions(getEmptyPrescription());
      setValue('prescription', JSON.stringify(getEmptyPrescription()));
      setSaveMyPres(false);
      // setValue('prescriptionName', '');
    };

    // ===== 弹窗处理函数 =====
    // 处理弹窗中的处方选中
    const handlePresSelect = (pres: CustomerPrescription) => {
      setSelectedPresInPopup(pres);
    };

    const checkAdd = (presType: string, _odAdd: string, _osAdd: string) => {
      let odAdd = _odAdd || '0.00';
      let osAdd = _osAdd || '0.00';
      if (presType == EPrescriptionType.PROGRESSIVE) {
        // ②处方类型为Progressive时，仅有一侧的ADD值在可选范围时，OD、OS均使用此ADD值
        // ③处方类型为Progressive时，左右眼ADD值均在可选范围且值不同时，ADD值忽略不填
        if (odAdd != osAdd) {
          if (odAdd != '0.00' && osAdd != '0.00') {
            odAdd = '0.00';
            osAdd = '0.00';
          } else if (odAdd != '0.00') {
            osAdd = odAdd;
          } else {
            odAdd = osAdd;
          }
        }
      } else if (
        presType == EPrescriptionType.SINGLE_VISION ||
        presType == EPrescriptionType.SUNGLASSES_PRESCRIPTION
      ) {
        // ④处方类型为Sunglasses Prescription时，ADD值忽略不填
        odAdd = '';
        osAdd = '';
      }
      return { odAdd, osAdd };
    };

    const handleScanPrescription = (data: ILoadPR) => {
      const { prescription: scanPrescription, presType, prescriptions_image } = data;
      logger.warn('handleScanPrescription', scanPrescription);
      if (!scanPrescription.pd && !scanPrescription.pd_l && !scanPrescription.pd_r) {
        // 识别出的pd结果为空，pd的勾选状态和当前页面的pd勾选状态保持一致
        // TODO 这里实际不会生效，以为单双PD在给表单组件传递处方时表单组件会重新进行判断。这里必要性不大暂时不处理
        scanPrescription.hasTwoPd = prescriptionOptions.hasTwoPd;
      }
      if (presType && presType !== prescriptionType) {
        setPrescriptionType(presType as EPrescriptionType);
      }
      // add值校验
      const _presType = presType || prescriptionType;
      const { odAdd, osAdd } = checkAdd(
        _presType,
        scanPrescription.od_add,
        scanPrescription.os_add,
      );
      scanPrescription.od_add = odAdd;
      scanPrescription.os_add = osAdd;

      // CYL值为0时，忽略AXIS，要在cyl值校验之后再判断axis
      if (
        scanPrescription.od_cyl == 'SPH/DS' ||
        scanPrescription.od_cyl == '0.00' ||
        !scanPrescription.od_cyl
      ) {
        scanPrescription.od_axis = 'None';
      }
      if (
        scanPrescription.os_cyl == 'SPH/DS' ||
        scanPrescription.os_cyl == '0.00' ||
        !scanPrescription.os_cyl
      ) {
        scanPrescription.os_axis = 'None';
      }

      if (prescriptions_image) {
        setPrescriptionsImage(prescriptions_image);
      }
      loadPrescription(
        {
          prescription: { ...getEmptyPrescription(), ...scanPrescription },
          name: '',
        },
        _presType,
      );
    };

    // 处理弹窗确认按钮点击
    const handleConfirmSelection = (presInPopup?: CustomerPrescription) => {
      const innerSelectedPres = presInPopup || selectedPresInPopup;
      if (innerSelectedPres) {
        setSelectedPres(innerSelectedPres);
        if (!presInPopup) {
          setScanInfo(null);
        }
        loadPrescription(innerSelectedPres);
        setValue('prescriptionName', innerSelectedPres.name);
        setSaveMyPres(true);
      }
    };

    /**
     * 根据处方类型缓存处方信息
     */
    const getCachePres = () => {
      const key = ZL_PRESCRIPTION_DATA;
      const cacheValue = localStorage.getItem(key);
      try {
        logger.warn('getCachePres====', cacheValue, prescriptionType);
        if (cacheValue) {
          const data = JSON.parse(cacheValue);
          if (data[prescriptionType]) {
            if (data[prescriptionType].pd && Number(data[prescriptionType].pd) > 0) {
              data[prescriptionType].pd = Number(data[prescriptionType].pd).toFixed(2);
            }
            if (data[prescriptionType].recordId) {
              setSession('saveScannedPres', {
                ...data[prescriptionType],
                pre_scan_scene_type: 'cache_use',
              });
              delete data[prescriptionType].recordId;
            }

            logger.warn('getCachePres====', data[prescriptionType]);

            loadPrescription({ prescription: data[prescriptionType], name: '' });
          }
        }
      } catch (e) {
        localStorage.removeItem(key);
        logger.warn('getCachePres====', e, key);
      }
    };
    useEffect(() => {
      getCachePres();
    }, []);
    /**
     * 设置当前表单缓存
     */
    const setCachePres = (presValue: IPrescriptionOptions, recordId?: string) => {
      const key = ZL_PRESCRIPTION_DATA;
      const cacheValue = localStorage.getItem(key);
      try {
        const data = cacheValue ? JSON.parse(cacheValue) : {};
        data[prescriptionType] = { ...presValue };
        data[prescriptionType].saveMyPres = false;
        data[prescriptionType].name = '';
        data[prescriptionType].id = '';
        data[prescriptionType].prescriptions_image = '';
        if (recordId) {
          data[prescriptionType].recordId = recordId;
        }
        localStorage.setItem(key, JSON.stringify(data));
      } catch (error) {
        logger.warn('setCachePres====', error, key);
        localStorage.removeItem(key);
      }
    };

    // 加载选择的处方
    const loadPrescription = (pres: CustomerPrescription, presType?: string) => {
      // 使用getEmptyPrescription作为基础，确保所有必选字段都存在
      const emptyPrescription = getEmptyPrescription();
      // 合并处方数据
      const prescription = { ...emptyPrescription, ...pres.prescription };
      checkPrescription(prescription, {
        presType: presType || prescriptionType,
        prescription: {
          is_two_pd: hasTwoPd(prescription),
        },
        usePd: {
          min_pd: Number(lensGoodsItem?.product?.pd_min) || 54,
          max_pd: Number(lensGoodsItem?.product?.pd_max) || 78,
        },
      });
      logger.warn('loadPrescription====', prescription);
      setPrescriptionOptions(prescription);
      setValue('prescription', JSON.stringify(prescription));
      setTimeout(() => {
        trigger('prescription');
      });
      setIsSelectPresDialogOpen(false);
    };

    // 关闭弹窗时重置选中状态
    const handleClosePopup = () => {
      setIsSelectPresDialogOpen(false);

      if (!selectedPresInPopup && myPresList.length > 0) {
        setSelectedPresInPopup(myPresList[0]);
      }
    };

    // ===== 确认处方相关函数 =====

    // 确认处方数据
    const confirmPrescription = async (): Promise<void> => {
      let isValid = await trigger();
      const data = getValues();
      logger.log('confirmPrescription', isValid);
      if (!isValid) {
        setTimeout(() => {
          scrollToError();
        });
        return;
      }

      // 处理表单数据
      const formData = JSON.parse(data.prescription) as IPrescriptionOptions;

      isValid = validateUnion(formData, getApiPrescriptionType(prescriptionType));
      if (!isValid) {
        setUnionDialogOpen(true);
        return;
      }

      // 如果用户选择保存处方
      if (saveMyPres) {
        if (!token) {
          handleOpenLogin('prescription', '保存我的处方');
          return;
        }
      }

      setPrescriptionOptions(formData);

      // 设置处方数据并打开确认弹窗
      setIsConfirmDialogOpen(true);

      SD_PrescriptionEnterPage_Submit_Click(JSON.stringify(formData));
    };

    // 确认处方选择
    const confirm = async () => {
      // 处理处方识别标识和缓存
      // const recordId = prescriptionOptions.pre_scan_id;
      // if (recordId) {
      //   prescriptionOptions.pre_scan_id = recordId;
      // }

      try {
        const flag = await savePrescriptionToAccount(prescriptionOptions);
        if (flag === false) {
          setIsConfirmDialogOpen(false);
          return;
        }
      } catch (error) {
        logger.error('保存处方失败', error);
      }

      // 缓存处方到本地
      setCachePres(prescriptionOptions);

      setPrescription(prescriptionOptions);
      // 将处方数据发送给父组件
      onSubmitPrescription({
        ...prescriptionOptions,
      });

      // 发送GA事件
      trackEvent(
        {
          eventName: 'prescription_modal_confirm',
          properties: {
            event_category: 'ADDPRESCRIPTION',
            event_label: prescriptionType,
          },
        },
        [ProviderType.GA4],
      );

      // 关闭确认弹窗
      setIsConfirmDialogOpen(false);
    };

    // ===== 副作用 =====
    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      confirmPrescription,
    }));

    // 组件挂载时获取用户处方列表
    useEffect(() => {
      if (token) {
        getUserPresList();
      }
    }, [token]);

    // ===== 埋点 =====
    function SD_PrescriptionEnterPage_View(is_have_scan_prescription: boolean) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_View',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              is_magnification_page: false,
              is_have_scan_prescription,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }
    function SDPrescriptionEnterPageParamIntroductionClick(param_introduction_type: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_ParamIntroduction_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              is_magnification_page: false,
              param_introduction_type,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }
    function SDPrescriptionEnterPageParamValueClick(param_value_type: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_ParamValue_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              is_magnification_page: false,
              param_value_type,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }
    function SDPrescriptionEnterPageCheckClick(check_type: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_Check_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              check_type,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }

    function SD_PrescriptionEnterPage_Submit_Click(user_prescription_detail: string) {
      try {
        trackEvent(
          {
            eventName: 'PrescriptionEnterPage_Submit_Click',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type_name: presTypeOptionName,
              prescription_type: getApiPrescriptionType(prescriptionType),
              user_prescription_detail,
              frame_price: parseFloat(goodsFramePriceInfo?.price),
              current_frame_price: parseFloat(goodsFramePriceInfo?.sell_price),
              currency: currency.name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error('👀', e);
      }
    }
    function SDPrescriptionEnterPagePrescriptionIntroductionClick(
      btn_name: string,
      prescription: IPrescriptionOptions,
    ) {
      trackEvent(
        {
          eventName: 'PrescriptionEnterPage_PrescriptionIntroduction_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            user_prescription_detail: JSON.stringify(prescription),
            btn_name,
            is_magnification_page: false,
          },
        },
        [ProviderType.SENSORS],
      );
    }

    const toterms = () => {
      window.open('/terms-conditions', '_blank');
    };

    const confirmPresContent = () => {
      return (
        <>
          <div className="msg-box p-[8px_9px] xl:p-[10px] bg-[#fff9d7] rounded-[6px] mb-[6px] xl:mb-[14px]">
            <div className="msg-item text-[12px] xl:text-[14px]">{t('sphPlusTip')}</div>
          </div>
          <PrescriptionTable
            prescription={{
              prescription: prescriptionOptions,
              prescription_type: getApiPrescriptionType(prescriptionType),
            }}
            isReadOnly
            onDelete={() => {}}
            onEdit={() => {}}
            onUploadImg={() => {}}
            showGray
            showError
            showPrism
          />
          {userInfo?.is_agreement == 2 && (
            <p className="agreement text-[12px] xl:text-[14px] mt-[14px]">
              {t('byClickingConfirmYouAgreeToOur')}{' '}
              <span className="underline cursor-pointer text-primary-purple" onClick={toterms}>
                {t('termsAndConditions')}
              </span>
              .{' '}
              {t(
                'iCertifyThatTheWearerIsOver16YearsOldAndThatTheyAreNotRegisteredBlindOrPartiallySighted',
              )}
            </p>
          )}
        </>
      );
    };

    const presSelectFormMethods = useForm({
      mode: 'onSubmit',
    });
    const prescriptionItemClassName =
      'flex-1 flex items-center justify-center p-[12px] xl:py-[8px] xl:w-[319px] border border-gray-300 hover:border-[#808080] bg-white rounded-lg cursor-pointer';
    const iconContainerClassName = 'mr-[10px] shrink-0 basis-[40px] xl:basis-[32px]';
    const textClassName = 'f12 flex-1';
    const prImageClassName = 'w-[40px] h-[40px] xl:w-[32px] xl:h-[32px]';
    const handlePresSelectChange = (event: SelectChangeEvent<string>) => {
      const { value } = event.target;
      const innerSelectedPres = myPresList.find((item) => item.id === Number(value));
      if (innerSelectedPres) {
        handleConfirmSelection(innerSelectedPres);
      }
    };
    const renderChooseMine = () => {
      return (
        <div
          className="flex-1 basis-[100px] shrink-0 xl:basis-[374px] xl:max-w-[374px]"
          onClick={chooseMyPrescription}
        >
          {isLoading ? (
            // 骨架屏效果
            <div className="px-[12px] border border-[#dce1e5] rounded-[8px] bg-white h-[66px] xl:h-[50px] w-full flex justify-between items-center">
              <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-4 animate-pulse" />
            </div>
          ) : (
            (() => {
              if (!token) {
                return isWap && enablePrescriptionRecognition ? (
                  <div className={`${prescriptionItemClassName}`}>
                    <div className={iconContainerClassName}>
                      <Image
                        src="/lens-process/select_pres.png"
                        alt={t('prescription')}
                        className={prImageClassName}
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className={textClassName}>
                      {`${t('chooseMyPrescription1')} `}
                      <br className="xl:hidden" />
                      {t('chooseMyPrescription2')}
                    </div>
                  </div>
                ) : (
                  <div className="box-border px-[10px] border border-[#dce1e5] hover:border-[#808080] rounded-[8px] bg-white h-[66px] xl:h-[50px] flex items-center cursor-pointer">
                    <div className={iconContainerClassName}>
                      <Image
                        src="/lens-process/select_pres.png"
                        alt={t('prescription')}
                        className={prImageClassName}
                        width={40}
                        height={40}
                      />
                    </div>
                    <span className="text-[14px] flex-1">{t('haveASavedPrescription')}</span>
                    <div className="flex items-center">
                      <span className="text-[14px] whitespace-nowrap">{t('signIn')}</span>
                      {<RightArrowOutlined className={stepCommonClassName.arrowIcon} />}
                    </div>
                  </div>
                );
              }

              if (isWap) {
                // 手机端 未启用处方识别样式暂时注释，统一用启用状态下的样式
                return (
                  <div className={`${prescriptionItemClassName}`}>
                    <div className={iconContainerClassName}>
                      <Image
                        src="/lens-process/select_pres.png"
                        alt={t('prescription')}
                        className={prImageClassName}
                        width={40}
                        height={40}
                      />
                    </div>
                    <div className={textClassName}>
                      {myPresList.length === 0 ? t('noSaved') : `${t('chooseMyPrescription1')} `}
                      {enablePrescriptionRecognition ? <br className="xl:hidden" /> : ' '}
                      {myPresList.length === 0
                        ? t('prescriptionSmall')
                        : t('chooseMyPrescription2')}
                    </div>
                  </div>
                );
              } else {
                if (myPresList.length === 0) {
                  return (
                    <div className="box-border px-[12px] border border-[#dce1e5] rounded-[8px] bg-white h-[50px] flex items-center">
                      <div className={iconContainerClassName}>
                        <Image
                          src="/lens-process/select_pres.png"
                          alt={t('prescription')}
                          className={prImageClassName}
                          width={40}
                          height={40}
                        />
                      </div>
                      <span className="text-[14px]">{t('noSavedPrescription')}</span>
                    </div>
                  );
                }
                return (
                  <div className="flex-1">
                    <Select
                      labelId="prescription-type"
                      id="prescription-type"
                      value={selectedPres?.id || ''}
                      name="pres_id"
                      control={presSelectFormMethods.control}
                      onChange={handlePresSelectChange}
                      formItemClassName="mb-0"
                      size="small"
                      displayEmpty
                      clearable={false}
                      onClear={() => {
                        clearSelectPres();
                      }}
                      sx={{
                        '& .MuiSelect-icon': {
                          fontSize: '16px',
                          color: '#808080',
                        },
                      }}
                      onClick={(e) => {
                        // 阻止事件冒泡, 避免上层dom的事件触发 SDPrescriptionEnterPageChooseClick
                        e.stopPropagation();
                      }}
                      renderValue={(value) => {
                        const innerSelectedPres = myPresList.find((item) => item.id === +value);
                        return (
                          <div className="flex items-center">
                            <div className={iconContainerClassName}>
                              <Image
                                src="/lens-process/scan_pres.png"
                                alt={t('prescription')}
                                className={prImageClassName}
                                width={40}
                                height={40}
                              />
                            </div>
                            <div key={innerSelectedPres?.id} className={textClassName}>
                              {innerSelectedPres && innerSelectedPres.name}
                              {!innerSelectedPres && `${t('chooseMyPrescription')}`}
                            </div>
                          </div>
                        );
                      }}
                      className="!rounded-[8px] !border-gray-300"
                    >
                      {myPresList.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          <span>
                            {item.name}&nbsp;&nbsp;&nbsp;&nbsp;{item.updated_at}
                          </span>
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                );
              }
            })()
          )}
        </div>
      );
    };
    return (
      <div
        className={`edit-prescription-area h-full overflow-hidden relative ${
          nowComponent !== EDIT_PRESCRIPTION_COMPONENT ? 'hidden' : ''
        }`}
      >
        <div className="h-full">
          <div className="max-xl:px-[14px] flex justify-between items-center">
            <div className={`${stepCommonClassName.stepTitle} !pl-0`}>
              {t('enterYourPrescription')}
            </div>
            <div
              className="sub-title gap-[4px] items-center hidden xl:flex xl:pb-[30px] cursor-pointer  hover:text-primary-purple group"
              onClick={() => {
                setLearnPrescriptionDialogOpen(true);
                SDPrescriptionEnterPageParamIntroductionClick('PrescriptionParam');
              }}
            >
              <QuestionResponsive
                className={`${stepCommonClassName.questionIcon} group-hover:text-primary-purple`}
              />
              <span className=" text-12 underline">{t('learnHowToReadYourPrescription')}</span>
            </div>
          </div>

          <div className="mx-[14px] xl:mx-0 mb-[14px]">
            {/* 处方识别功能 */}
            {enablePrescriptionRecognition && (
              <PrescriptionRecognition
                prescriptionType={prescriptionType}
                presTypeOptionName={presTypeOptionName}
                prLoadAnimationTime={prLoadAnimationTime}
                onLoadPrescription={handleScanPrescription}
                renderChooseMine={renderChooseMine}
                enablePrescriptionRecognitionNoviceGuide={enablePrescriptionRecognitionNoviceGuide}
                prescriptionItemClassName={prescriptionItemClassName}
                iconContainerClassName={iconContainerClassName}
                textClassName={textClassName}
                prImageClassName={prImageClassName}
                abTestingData={abTestingData}
              />
            )}

            {/* 选择处方区域 */}
            {!enablePrescriptionRecognition && renderChooseMine()}
          </div>

          <div className="section-content px-[14px] py-[20px] xl:py-0 bg-white rounded-t-[12px] xl:px-0 xl:bg-transparent">
            <div className="sub-title flex gap-[4px] pb-[16px] items-center xl:hidden">
              <span className="font-bs text-16">{t('prescription')}</span>
              <QuestionResponsive
                className={stepCommonClassName.questionIcon}
                onClick={() => {
                  setLearnPrescriptionDialogOpen(true);
                  SDPrescriptionEnterPageParamIntroductionClick('PrescriptionParam');
                }}
              />
            </div>
            <PrescriptionForm
              className=""
              prescriptionType={getApiPrescriptionType(prescriptionType)}
              pd_min={lensGoodsItem?.product?.pd_min || '54'}
              pd_max={lensGoodsItem?.product?.pd_max || '78'}
              prescriptionOptions={prescriptionOptions}
              name="prescription"
              control={control}
              onOpen={handlePrescriptionSelectionOpen}
              onClickPdHelp={() => {
                setLearnPrescriptionTab('pd');
                setLearnPrescriptionDialogOpen(true);
                SDPrescriptionEnterPageParamIntroductionClick('PdHelp');
              }}
              onClickPrismHelp={() => {
                setLearnPrescriptionTab('prism');
                setLearnPrescriptionDialogOpen(true);
                SDPrescriptionEnterPageParamIntroductionClick('PrismParam');
              }}
              onPdCheckChange={() => {
                SDPrescriptionEnterPageCheckClick('2 PD Numbers');
              }}
              onAddPrism={() => {
                SDPrescriptionEnterPageCheckClick('Add Prism');
              }}
              onDeletePrism={() => {
                SDPrescriptionEnterPageCheckClick('Delete Prism');
              }}
            />

            {/* 保存处方区域 */}
            <div className=" border-[#d0d4db]">
              <div className="flex items-center">
                <Checkbox
                  label={selectedPres?.id ? t('updateMyPrescription') : t('saveMyPrescription')}
                  checked={saveMyPres}
                  isThinner
                  // fontSize={isWap ? 16 : 16}
                  onChange={(e) => {
                    handleSaveMyPresChange(e);
                    SDPrescriptionEnterPageCheckClick(
                      selectedPres?.id ? 'Update My Prescription' : 'Save My Prescription',
                    );
                  }}
                  sx={{ '& .MuiTypography-root': { fontSize: '14px' } }}
                />
              </div>
              {saveMyPres && (
                <div className="pb-[10px] xl:w-[374px]">
                  <Input
                    size="small"
                    placeholder={t('prescriptionNamePlaceholder')}
                    name="prescriptionName"
                    control={control}
                    formatInput={PRESCRIPTION_NAME_FORMAT_INPUT}
                    formItemClassName="mb-0"
                    onBlur={() => {
                      trigger('prescriptionName');
                    }}
                    rules={{
                      required: t('prescriptionNameRequired'),
                      pattern: {
                        value: PRESCRIPTION_NAME_PATTERN,
                        message: undefined,
                      },
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: 20,
                      },
                    }}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>

          {/* 添加留言按钮 */}
          <div className="max-xl:px-[14px] py-[24px] text-center xl:pt-[56px] xl:pb-[20px] xl:text-left">
            <p className="text-[14px] mb-[12px] hidden xl:block">
              {t('unableToMakeSureYourPrescription')}
            </p>
            <div
              className="f12 underline cursor-pointer xl:text-primary-purple inline-block"
              onClick={() => {
                setLeaveMsgDialogOpen(true);
                SDPrescriptionEnterPageParamIntroductionClick('Message');
              }}
            >
              {t('leaveAMessage')}
            </div>
          </div>
        </div>

        {/* 确认处方数据 */}
        <WapOnly>
          <Popup
            open={isConfirmDialogOpen}
            showDivider={false}
            title={t('doesThisMatchYourPrescription')}
            onClose={() => {
              setIsConfirmDialogOpen(false);
              SDPrescriptionEnterPagePrescriptionIntroductionClick('Close', prescriptionOptions);
            }}
            bodyClassName="!p-0"
          >
            <div className="relative">
              <div className="px-14 pb-[95px] max-h-[70vh] overflow-y-auto">
                {confirmPresContent()}
              </div>
              <div className="flex absolute bottom-0 z-2 bg-white left-0 w-full p-[15px] gap-[15px] justify-center shadow-[0px_-2px_10px_0px_rgba(204,204,204,0.5)]">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setIsConfirmDialogOpen(false);
                    SDPrescriptionEnterPagePrescriptionIntroductionClick(
                      'Edit',
                      prescriptionOptions,
                    );
                  }}
                  sx={{
                    '&.MuiButton-root': {
                      fontFamily: font.bb,
                    },
                  }}
                >
                  {t('edit')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    confirm();
                    SDPrescriptionEnterPagePrescriptionIntroductionClick(
                      'Confirm',
                      prescriptionOptions,
                    );
                  }}
                  sx={{
                    '&.MuiButton-root': {
                      fontFamily: font.bb,
                    },
                  }}
                >
                  {t('confirm')}
                </Button>
              </div>
            </div>
          </Popup>
        </WapOnly>
        <WebOnly>
          <Dialog
            translate="no"
            open={isConfirmDialogOpen}
            title={t('doesThisMatchYourPrescription')}
            onClose={() => {
              setIsConfirmDialogOpen(false);
              SDPrescriptionEnterPagePrescriptionIntroductionClick('Close', prescriptionOptions);
            }}
            fullWidth
            maxWidth="lg"
            cancelText={t('edit')}
            sx={{
              '& .MuiDialog-paper': {
                minWidth: '658px',
              },
            }}
            okText={t('confirm')}
            onOk={() => {
              confirm();
              SDPrescriptionEnterPagePrescriptionIntroductionClick('Confirm', prescriptionOptions);
            }}
            onCancel={() => {
              setIsConfirmDialogOpen(false);
              SDPrescriptionEnterPagePrescriptionIntroductionClick('Edit', prescriptionOptions);
            }}
          >
            <div className="">
              {confirmPresContent()}
              {/* <div className="flex justify-center mt-[36px] gap-[12px]">
                <Button
                  variant="outlined"
                  className="w-[180px] h-[40px]"
                  onClick={() => {
                    setIsConfirmDialogOpen(false);
                    SDPrescriptionEnterPagePrescriptionIntroductionClick(
                      'Edit',
                      prescriptionOptions,
                    );
                  }}
                >
                  {t('edit')}
                </Button>
                <Button
                  variant="contained"
                  className="w-[180px] h-[40px]"
                  onClick={() => {
                    confirm();
                    SDPrescriptionEnterPagePrescriptionIntroductionClick(
                      'Confirm',
                      prescriptionOptions,
                    );
                  }}
                >
                  {t('confirm')}
                </Button>
              </div> */}
            </div>
          </Dialog>
        </WebOnly>

        {/* 选择处方弹窗 */}
        <PopupResponsive
          open={isSelectPresDialogOpen}
          showDivider={false}
          title={t('myPrescriptions')}
          bodyClassName="bg-[#f5f6f7] !px-[8px]"
          childrenMaxHeight="60vh"
          onClose={handleClosePopup}
          okDisabled={!selectedPresInPopup}
          hasFooter
          okText={t('confirm')}
          onOk={() => {
            handleConfirmSelection();
            setIsSelectPresDialogOpen(false);
          }}
          sx={{
            '& .popup-title': {
              backgroundColor: '#f5f6f7',
            },
            '& .popup-content': {
              pt: '0',
            },
          }}
        >
          <div className="">
            <div className="pb-[40px]">
              {myPresList.map((pres) => (
                <div
                  key={pres.id}
                  className={`pb-[12px] cursor-pointer relative ${
                    selectedPresInPopup?.id === pres.id ? 'active' : ''
                  }`}
                  onClick={() => handlePresSelect(pres)}
                >
                  {selectedPresInPopup?.id === pres.id && (
                    <div className="absolute top-0 right-0 w-[40px] h-[16px] bg-primary-purple rounded-[0px_8px_0px_8px] flex justify-center items-center text-white">
                      <CheckOutlined />
                    </div>
                  )}
                  <div
                    className={`p-[12px] pt-[18px] border-[1.5px] ${selectedPresInPopup?.id === pres.id ? 'border-primary-purple' : 'border-transparent'} rounded-lg bg-white`}
                  >
                    <PrescriptionTable
                      prescription={pres}
                      isReadOnly
                      showImage
                      showHeaderInfo
                      onDelete={() => {}}
                      onEdit={() => {}}
                      onUploadImg={() => {}}
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* <div className="fixed bottom-0 left-0 w-full h-[69px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] bg-white flex justify-center items-center">
              <Button
                variant="contained"
                className="w-[285px] h-[40px]"
                disabled={!selectedPresInPopup}
                onClick={() => {
                  handleConfirmSelection();
                  setIsSelectPresDialogOpen(false);
                }}
              >
                {t('confirm')}
              </Button>
            </div> */}
          </div>
        </PopupResponsive>

        {/* 学习处方弹窗 */}
        <LearnPrescriptionDialog
          open={learnPrescriptionDialogOpen}
          initTab={learnPrescriptionTab}
          onClose={() => setLearnPrescriptionDialogOpen(false)}
        />

        {/* 留言弹窗 */}
        <LeaveMsg open={leaveMsgDialogOpen} onClose={() => setLeaveMsgDialogOpen(false)} />

        <UnionDialog
          unionShow={unionDialogOpen}
          onOk={() => setUnionDialogOpen(false)}
          onClose={() => setUnionDialogOpen(false)}
        />
      </div>
    );
  },
);

export default EditPrescription;
